import { NPI_SUGGESTION_STAGES, type NpiSuggestionStage } from './types';

export type ProgressStepState = 'done' | 'active' | 'pending';

/**
 * The steps a run shows, in order, in the words the person sees. A search for
 * a name the person typed reads that name, not their email, and says so.
 */
export function progressSteps(
  fromTypedName: boolean
): readonly { stage: NpiSuggestionStage; label: string }[] {
  return [
    {
      stage: 'reading',
      label: fromTypedName ? 'Reading the name you entered' : 'Reading your email',
    },
    { stage: 'searching', label: 'Searching the NPI registry' },
    { stage: 'comparing', label: 'Comparing matches' },
  ];
}

/**
 * Where one step stands against the run's current stage. Before the first
 * hint arrives the run is treated as reading, so the stepper never sits on an
 * empty first row while the run is already working.
 */
export function progressStepState(
  step: NpiSuggestionStage,
  current: NpiSuggestionStage | null
): ProgressStepState {
  const stepIndex = NPI_SUGGESTION_STAGES.indexOf(step);

  const currentIndex = NPI_SUGGESTION_STAGES.indexOf(current ?? 'reading');

  if (stepIndex < currentIndex) {
    return 'done';
  }

  return stepIndex === currentIndex ? 'active' : 'pending';
}

/** How long a run may take before the screen says it is still going. */
export const SLOW_RUN_AFTER_MS = 10_000;

/**
 * The further along of two reports of a run's stage. A hint and a read can
 * arrive in either order, and the stepper must never step backward.
 */
export function laterStage(
  first: NpiSuggestionStage | null,
  second: NpiSuggestionStage | null
): NpiSuggestionStage | null {
  if (!first || !second) {
    return first ?? second;
  }

  return NPI_SUGGESTION_STAGES.indexOf(first) >= NPI_SUGGESTION_STAGES.indexOf(second)
    ? first
    : second;
}
